import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { TicketDataService } from '../../ticket.data.service';
import { ToasterService } from '../../../../services/core/toaster.service';

/** A scope item as offered in the picker. */
interface PickerItem {
  id: number;
  kind: 'material' | 'service';
  item_name: string;
  description: string;
  vendor_name: string;
  quantity: number | null;
  price: number | null;
  scope_status: string;
  scope_name: string;
}

/** An item already attached to the ticket. */
interface AttachedItem extends PickerItem {
  /** Null while the row is only buffered client-side (ticket not saved yet). */
  id: any;
  job_scope_item_id: number;
  linked_scope_id: any;
  linked_scope_name: string;
  is_linked: boolean;
}

/**
 * Materials & Services on a ticket.
 *
 * "+" opens a picker of the job's scope items, split into Materials
 * (item_type = App\Models\Product) and Services (App\Models\Service). Ticking
 * rows and pressing "Add Selected" attaches them in one call and closes the
 * picker; attached items render as cards below.
 *
 * Attachments have no editable fields — a card is drawn entirely from the
 * job_scope_items columns copied into tickets_item on attach.
 */
@Component({
  selector: 'app-ticket-materials-services',
  templateUrl: './ticket-materials-services.component.html',
  styleUrls: ['./ticket-materials-services.component.scss'],
  standalone: false
})
export class TicketMaterialsServicesComponent {

  /** Items already attached (`model.ticket_items`). */
  @Input() materialServices: any;

  /** View-only — hides "+", the picker and the remove buttons. */
  @Input() isReadOnly: boolean = false;

  /** The job whose scope items can be attached. No job ⇒ nothing to pick. */
  @Input() jobId: number | string | null = null;

  /** Required to attach/remove; absent while a ticket is still being created. */
  @Input() ticketId: number | string | null = null;

  /** Emits the attached list so the parent can keep its tab badge in sync. */
  @Output() callback = new EventEmitter<any[]>();

  // ── attached items ────────────────────────────────────────────────────────
  items: AttachedItem[] = [];
  listSearch = '';

  // ── picker ────────────────────────────────────────────────────────────────
  pickerOpen = false;
  /** Guards the one-time load when the picker shows itself on an empty tab. */
  private pickerLoaded = false;
  activeTab: 'material' | 'service' = 'material';
  pickerItems: PickerItem[] = [];
  pickerCounts = { material: 0, service: 0 };
  pickerSearch = '';
  pickerLoading = false;
  selectedIds = new Set<number>();
  jobScopes: any[] = [];
  linkedScopeId: any = null;

  private lastEmitted: any[] | null = null;

  constructor(
    public ticketDataService: TicketDataService,
    private toasterService: ToasterService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['materialServices']) {
      const rows = changes['materialServices'].currentValue;

      // Ignore the echo of our own emit — the detail page writes the emitted
      // array straight back into this input, which would otherwise reset the list.
      if (rows !== this.lastEmitted) {
        this.items = Array.isArray(rows) ? rows : [];
      }
    }

    // The job can change under us in the Tickets module, where the user picks
    // the module and reference by hand. Everything cached here belongs to the
    // previous job, so it is discarded rather than shown against the new one.
    if (changes['jobId'] && !changes['jobId'].firstChange) {
      this.resetForNewJob(changes['jobId'].previousValue);
    }

    // Runs for EVERY input change, not just materialServices: jobId usually
    // resolves a beat after the first bind, and returning early on that pass
    // would leave the picker showing an empty list it never tried to load.
    this.syncPickerVisibility();
  }

  /**
   * Drop everything tied to the job we just left.
   *
   * Buffered rows (id === null) were chosen against the old job and would be
   * attached to the new one on save, so they go too. Rows already persisted are
   * left alone — the server owns those, and the ticket has not moved.
   */
  private resetForNewJob(previousJobId: any): void {
    this.pickerLoaded = false;
    this.pickerItems = [];
    this.pickerCounts = { material: 0, service: 0 };
    this.pickerSearch = '';
    this.selectedIds.clear();
    this.jobScopes = [];
    this.linkedScopeId = null;

    if (!previousJobId) {
      return;
    }

    const persisted = this.items.filter(row => !!row.id);
    if (persisted.length !== this.items.length) {
      this.items = persisted;
      this.emit();
    }
  }

  /**
   * With nothing attached yet there is no list to show, so the picker takes the
   * whole tab — opening straight onto Materials / Services rather than an empty
   * toolbar. Once items exist the list leads and "+ Material" reopens the picker.
   */
  private syncPickerVisibility(): void {
    if (this.isPickerVisible && !this.pickerLoaded && this.jobId) {
      this.pickerLoaded = true;
      // Scope selection is hidden for now — restore with the <select>.
      // this.loadScopes();
      this.loadPickerItems();
    }
  }

  /** True when the picker occupies the tab — explicitly opened, or nothing to list. */
  get isPickerVisible(): boolean {
    return this.pickerOpen || (this.items.length === 0 && !!this.jobId && !this.isReadOnly);
  }

  /** "Back to list" only makes sense when there is a list to go back to. */
  get canReturnToList(): boolean {
    return this.items.length > 0;
  }

  /**
   * The picker only needs a job — selections are buffered client-side while the
   * ticket is still being created.
   */
  get canAddItems(): boolean {
    return !this.isReadOnly && !!this.jobId;
  }

  /** True once the ticket exists, i.e. attachments can be persisted immediately. */
  private get canPersist(): boolean {
    return !!this.ticketId;
  }

  // ── attached list ─────────────────────────────────────────────────────────

  /** Cards after the search box. */
  get visibleItems(): AttachedItem[] {
    const term = this.listSearch.trim().toLowerCase();

    if (!term) {
      return this.items;
    }

    return this.items.filter(item =>
      `${item.item_name} ${item.vendor_name}`.toLowerCase().includes(term)
    );
  }

  removeItem(item: AttachedItem): void {
    if (this.isReadOnly || !item) {
      return;
    }

    const previous = this.items;
    this.items = this.items.filter(row => row !== item);
    this.emit();

    // Buffered rows (chosen before the ticket was saved) have no server id yet.
    if (!this.canPersist || !item.id) {
      return;
    }

    this.ticketDataService.deleteTicketItem(item.id).subscribe({
      error: () => {
        // Put it back — the server still has it.
        this.items = previous;
        this.emit();
        this.toasterService.showError('Could not remove the item.');
      },
    });
  }

  // ── picker ────────────────────────────────────────────────────────────────

  openPicker(): void {
    if (!this.canAddItems) {
      return;
    }
    this.pickerOpen = true;
    this.pickerLoaded = true;
    this.activeTab = 'material';
    this.pickerSearch = '';
    this.selectedIds.clear();
    this.linkedScopeId = null;
    // Scope selection is hidden for now — restore with the <select>.
    // this.loadScopes();
    this.loadPickerItems();
  }

  closePicker(): void {
    this.pickerOpen = false;
    this.selectedIds.clear();
  }

  setTab(tab: 'material' | 'service'): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.loadPickerItems();
  }

  onPickerSearch(): void {
    this.loadPickerItems();
  }

  private loadScopes(): void {
    this.ticketDataService.getJobScopes(this.jobId).subscribe({
      next: (response: any) => {
        this.jobScopes = response?.data?.data ?? response?.data ?? [];
      },
      error: () => (this.jobScopes = []),
    });
  }

  private loadPickerItems(): void {
    this.pickerLoading = true;
    this.ticketDataService.searchJobScopeItems({
      job_id: this.jobId,
      search: this.pickerSearch,
      kind: this.activeTab,
    }).subscribe({
      next: (response: any) => {
        // A resource collection answers { data: [...], counts: {...} } — `data`
        // is the array itself, NOT another envelope. Still tolerate a wrapped
        // shape, since most endpoints here return { data: { ... } }.
        const items = Array.isArray(response?.data)
          ? response.data
          : (response?.data?.data ?? []);
        const counts = response?.counts ?? response?.data?.counts ?? null;

        this.pickerItems = items;
        this.pickerCounts = counts ?? { material: 0, service: 0 };
        this.pickerLoading = false;
      },
      error: () => {
        this.pickerItems = [];
        this.pickerLoading = false;
      },
    });
  }

  /** Already attached to this ticket — shown ticked and locked, not re-addable. */
  isAlreadyAdded(item: PickerItem): boolean {
    return this.items.some(row => row.job_scope_item_id === item.id);
  }

  isSelected(item: PickerItem): boolean {
    return this.selectedIds.has(item.id) || this.isAlreadyAdded(item);
  }

  toggleSelection(item: PickerItem): void {
    // Unticking something already on the ticket detaches it, so the checkbox
    // stays a true toggle rather than a one-way door.
    const attached = this.items.find(row => row.job_scope_item_id === item.id);
    if (attached) {
      this.removeItem(attached);
      return;
    }

    if (this.selectedIds.has(item.id)) {
      this.selectedIds.delete(item.id);
    } else {
      this.selectedIds.add(item.id);
    }
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  /** Attach every ticked row, then close the picker. */
  addSelected(): void {
    if (!this.selectedCount || !this.canAddItems) {
      return;
    }

    // No ticket yet (create dialog): keep the selection in memory and let the
    // ticket save attach it. Nothing is written until the ticket exists.
    if (!this.canPersist) {
      this.bufferSelected();
      this.closePicker();
      return;
    }

    this.ticketDataService.addTicketItems(
      this.ticketId,
      Array.from(this.selectedIds),
      this.linkedScopeId,
    ).subscribe({
      next: (response: any) => {
        // The server returns the ticket's full item list after the attach.
        const body = response?.data ?? response;
        this.items = body?.data ?? this.items;
        this.emit();
        this.closePicker();
      },
      error: () => this.toasterService.showError('Could not add the selected items.'),
    });
  }

  /**
   * Turn the picker selection into cards without saving. The shape matches what
   * the server returns, so the card template renders either kind identically.
   */
  private bufferSelected(): void {
    const scope = this.jobScopes.find(row => row.id === this.linkedScopeId);

    const chosen = this.pickerItems
      .filter(item => this.selectedIds.has(item.id))
      .filter(item => !this.items.some(row => row.job_scope_item_id === item.id))
      .map(item => ({
        ...item,
        id: null as any,
        job_scope_item_id: item.id,
        linked_scope_id: this.linkedScopeId,
        linked_scope_name: scope?.scope_name ?? '',
        is_linked: !!this.linkedScopeId,
      })) as AttachedItem[];

    this.items = [...this.items, ...chosen];
    this.emit();
  }

  private emit(): void {
    this.lastEmitted = this.items;
    this.callback.emit(this.items);
  }
}
